// Shared data-shaping for the scan report — used by BOTH the slide-deck and the single-page
// (PDF) report layouts so the two presentations are driven by identical numbers and copy.
// Lifted verbatim from the original inline logic in PipsqueakWidget.renderResults().

import { leadMagnetType } from "../../v2/lib/leadMagnetTypes";

const SEV_ORDER = { critical: 0, high: 1, medium: 2, low: 3, info: 4 };

const BAND_RANK = { critical: 0, high: 1, medium: 2, low: 3 };

// Band implied by the number alone. Kept for callers that genuinely only have a score,
// but prefer riskBandFor() below — deriving the label purely from the number is what put
// "Critical" next to a "0 Critical" counter on the results panel.
export function getRiskLevel(score) {
  if (score >= 80) return 'low';
  if (score >= 60) return 'medium';
  if (score >= 40) return 'high';
  return 'critical';
}

/**
 * A representative score for each band, for feeding the various threshold-based
 * style/colour helpers around the app without rewriting each one's palette.
 *
 * Display the REAL score to the user — this exists only so a style function that switches
 * on a number resolves to the tier the band says, rather than to whatever tier the raw
 * number falls in. Those two disagree exactly when the band has been clamped, which is
 * the case we need to render correctly.
 */
export function bandStyleScore(band) {
  switch (band) {
    case 'low': return 90;
    case 'medium': return 70;
    case 'high': return 50;
    case 'critical': return 20;
    default: return 90;
  }
}

/**
 * The risk band to display for a scan result.
 *
 * Always prefer the backend's `overallRisk`: it is already clamped so the label can never
 * be more severe than the worst finding actually present. When it is missing (older
 * payloads), fall back to the score threshold but apply the same clamp locally using
 * whichever severity information the payload carries.
 *
 * Without this, a low score always rendered "Critical" regardless of what was found — a
 * domain with twenty high-severity issues and zero criticals still scores 30 and would
 * otherwise be labelled critical, contradicting the counters beside it.
 *
 * @param {object} data scan result — may carry overallRisk / worstSeverity / severityCounts
 * @param {number} [scoreOverride] score to use when data has none
 */
export function riskBandFor(data, scoreOverride) {
  const fromBackend = data?.overallRisk;
  if (fromBackend && BAND_RANK[fromBackend] !== undefined) return fromBackend;

  const score = Number.isFinite(scoreOverride) ? scoreOverride : data?.riskScore;
  const band = getRiskLevel(Number.isFinite(score) ? score : 100);

  // Clamp to the worst severity we can see, so the fallback can't reintroduce the bug.
  const worst = data?.worstSeverity
    || (data?.severityCounts?.critical > 0 ? 'critical'
      : data?.severityCounts?.high > 0 ? 'high'
        : data?.severityCounts?.medium > 0 ? 'medium'
          : data?.severityCounts ? 'low' : null);

  if (!worst || BAND_RANK[worst] === undefined) return band;
  // Higher rank = less severe; take the less severe of the two.
  const finalRank = Math.max(BAND_RANK[band], BAND_RANK[worst]);
  return Object.keys(BAND_RANK).find((k) => BAND_RANK[k] === finalRank) || 'low';
}

export function getGrade(score) {
  if (score >= 90) return 'A';
  if (score >= 80) return 'B';
  if (score >= 70) return 'C';
  if (score >= 60) return 'D';
  return 'F';
}

// Per-category letter grade from its worst unresolved finding.
/** "✓ 5 passed · ⚠ 2 issues" for a category's exposure row. */
export function categoryTally(cat) {
  const fs = cat.findings || [];
  const pass = fs.filter(f => f.status === 'pass').length;
  const issues = fs.filter(f => f.status === 'fail' || f.status === 'warning').length;
  const parts = [];
  if (pass) parts.push(`✓ ${pass} passed`);
  if (issues) parts.push(`⚠ ${issues} issue${issues === 1 ? '' : 's'}`);
  return parts.join(' · ') || `${fs.length} check${fs.length === 1 ? '' : 's'}`;
}

export const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

export function categoryGrade(cat) {
  const fs = (cat.findings || []).filter(f => f.status !== 'pass');
  if (fs.some(f => f.severity === 'critical')) return 'F';
  if (fs.some(f => f.severity === 'high')) return 'D';
  if (fs.some(f => f.severity === 'medium')) return 'C';
  if (fs.length > 0) return 'B';
  return 'A';
}

// Translate a technical finding into business-impact copy for the narrative section.
function impactCopy(f, domain) {
  const t = `${f.title} ${f.categoryName || ''}`.toLowerCase();
  if (t.includes('dmarc') || t.includes('spf') || t.includes('spoof')) return {
    icon: '📧', head: 'Email Domain Impersonation',
    body: `Weak email authentication means attackers can send mail that appears to come from ${domain} — targeting your customers, partners, or payment recipients with convincing fraud.`
  };
  if (t.includes('dkim')) return {
    icon: '✉️', head: 'Email Integrity Unverified',
    body: `Without DKIM signing, recipients have no cryptographic proof your email is genuine — invoices, contracts, and client communications are easier to forge.`
  };
  if (t.includes('typo') || t.includes('lookalike') || t.includes('squat')) return {
    icon: '🛡️', head: 'Lookalike Domains — Active Threat',
    body: `Lookalike domains can be registered by bad actors to launch spear-phishing campaigns that impersonate ${domain} to your customers and staff.`
  };
  if (t.includes('breach') || t.includes('credential') || t.includes('dark web')) return {
    icon: '🔑', head: 'Exposed Credentials = Live Risk',
    body: `Credentials tied to your domain circulating in breach data are in attacker toolkits right now — a direct path into your systems without MFA enforcement.`
  };
  if (t.includes('header') || t.includes('hsts') || t.includes('cookie') || t.includes('csp')) return {
    icon: '🌐', head: 'Website Hardening Gaps',
    body: `Missing web security protections leave ${domain} more exposed to interception and cross-site attacks — fixable in hours, but visible to every attacker scanning your site.`
  };
  if (t.includes('ssl') || t.includes('tls') || t.includes('certificate') || t.includes('encrypt')) return {
    icon: '🔒', head: 'Encryption Weaknesses',
    body: `Outdated or misconfigured encryption undermines the confidentiality of every session with your site and can trigger browser trust warnings for your visitors.`
  };
  if (t.includes('dnssec') || t.includes('dns') || t.includes('nameserver')) return {
    icon: '🗺️', head: 'DNS Trust Gaps',
    body: `Without DNS-level protections, responses for ${domain} are easier to tamper with — enabling traffic redirection your users would never notice.`
  };
  return {
    icon: '⚠️', head: f.title,
    body: `${f.description || 'This gap is visible from the outside.'} Left unresolved, this is exactly the kind of weakness attackers actively scan for.`
  };
}

// Pull a named section (e.g. "Recommended Actions") out of a finding's detail text.
function extractSection(detail, keys) {
  if (!detail) return null;
  const lines = detail.split('\n');
  for (let i = 0; i < lines.length; i++) {
    const l = lines[i].trim().toLowerCase();
    if (keys.some(k => l.startsWith(k))) {
      const inline = lines[i].split(':').slice(1).join(':').replace(/\*\*/g, '').trim();
      if (inline) return inline;
      const out = [];
      for (let j = i + 1; j < lines.length && out.length < 2; j++) {
        const s = lines[j].trim();
        if (!s) { if (out.length) break; continue; }
        if (/^([-•*]|\d+\.)\s/.test(s)) out.push(s.replace(/^([-•*]|\d+\.)\s*/, '').replace(/\*\*/g, ''));
        else { out.push(s.replace(/\*\*/g, '')); break; }
      }
      return out.length ? out.join('; ') : null;
    }
  }
  return null;
}

export function fixFor(f) {
  return extractSection(f.detail, ['recommended actions', 'immediate recommended', 'immediate fix', 'recommendations', 'still recommended actions'])
    || 'Remediate this finding following current security best practices, then re-scan to verify.';
}

export function whyFor(f) {
  return extractSection(f.detail, ['why this matters', 'why this is a', 'why this is'])
    || f.description || 'Reduces your external attack surface.';
}

// Compute everything both layouts need from the raw scan `data` for `domain`.
//
// `reportType` is the lead magnet the scan was run as. It decides what the document calls
// itself: an email security widget runs the email module alone, so its report already
// contains nothing but mail findings — but it was still headed "External Security &
// Attack Surface Review", which promises a full scan and makes a focused report read as a
// broken one. Unknown or absent types fall back to the full-scan wording, which is what a
// scan with no widget behind it actually is.
export function shapeReport(data, domain, providerName, reportType) {
  const categories = data.categories || [];
  const totalFindings = categories.reduce((sum, cat) => sum + (cat.findings?.length || 0), 0);
  // Severity tiles count issues only. A passing check carries a severity too (the clean
  // breach result is "low"), and counting it put "1 LOW" on a report that found nothing
  // wrong — then called that pass a "hardening opportunity".
  const count = (sev) => categories.reduce((sum, cat) => sum + (cat.findings?.filter(f => f.status !== 'pass' && f.severity === sev).length || 0), 0);
  const criticalCount = count('critical');
  const highCount = count('high');
  const mediumCount = count('medium');
  const lowCount = count('low');
  // Prefer the backend's clamped band so the label agrees with the severity counters.
  const riskLevel = riskBandFor(data);
  const categoryCount = categories.length;

  const allFindings = categories.flatMap(cat => (cat.findings || []).map(f => ({ ...f, categoryName: cat.name })));
  const issueRows = allFindings
    .filter(f => f.status !== 'pass')
    .sort((a, b) => (SEV_ORDER[a.severity] ?? 9) - (SEV_ORDER[b.severity] ?? 9))
    .slice(0, 8);
  const passRows = allFindings.filter(f => f.status === 'pass').slice(0, 4);
  const failRows = allFindings.filter(f => f.status === 'fail').slice(0, 4);

  // The full good-and-bad lists. A report should read as complete whether the scan found
  // ten problems or none: the strengths are as much the result as the gaps.
  const strengths = allFindings.filter(f => f.status === 'pass');
  const gaps = allFindings
    .filter(f => f.status === 'fail' || f.status === 'warning')
    .sort((a, b) => (SEV_ORDER[a.severity] ?? 9) - (SEV_ORDER[b.severity] ?? 9));
  const passCount = strengths.length;
  const issueCount = allFindings.filter(f => f.status !== 'pass').length;

  const impactCards = [];
  const seen = new Set();
  for (const f of issueRows) {
    const c = impactCopy(f, domain);
    if (seen.has(c.head)) continue;
    seen.add(c.head);
    impactCards.push(c);
    if (impactCards.length >= 4) break;
  }
  const challengeRows = issueRows.slice(0, 5);

  // Managed coverage, when the caller attached it. Computed by the backend's asset
  // matcher (utils/assetMatcher.js) against the RMM devices a connector synced, so it is
  // only ever present for a scan run against a client organization — a prospect has no
  // managed estate to compare against, and the section stays out of their report.
  // Kept when there is anything to say: self-hosted systems to compare, or only
  // third-party ones, in which case the section explains where they live instead.
  const coverage = (data.coverage && (data.coverage.total || data.coverage.thirdParty))
    ? data.coverage
    : null;

  const magnet = leadMagnetType(reportType);

  return {
    data, domain, providerName,
    reportTitle: magnet.reportTitle,
    reportLede: magnet.reportLede,
    categories, totalFindings, criticalCount, highCount, mediumCount, lowCount,
    riskLevel, grade: getGrade(data.riskScore), categoryCount,
    allFindings, issueRows, passRows, failRows, impactCards, challengeRows,
    strengths, gaps, passCount, issueCount,
    coverage
  };
}
